import { Box, Grid, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography, Checkbox, FormControlLabel, TextField } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectBasketConditionalEntry,
    selectBasketConditionalExit,
    selectBasketId,
    selectBatchId,
    selectIceberg,
    selectOrderType,
    selectStrategyBuilderMode,
    selectStrategyGroup,
    selectTradeOrders,
    selectTradeProduct,
    selectWaitingForOrders,
} from '../../../selectors/strategyBuilder'
import { Broker_OrderType, broker_OrderTypeToJSON, Order, OrderStatus, OrderType, Product, TransactionType } from '../../../proto/generated/order'
import {
    updateBasketConditionalEntry,
    updateBasketConditionalExit,
    updateIceberg,
    updateOrderType,
    updateTradeProduct,
} from '../../../reducer/strategyBuilder.reducer'
import { NonInteractiveTooltip } from '../../custom'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { selectOrdersHistory } from '../../../selectors/orders'
import SlAndTargetInputs from '../slAndTargetInputs/slAndTargetInputs'
import { Condition } from '../../../proto/generated/nexus'
import { Position } from '../../../proto/generated/position'
import { getAnalyzedGroupedPosition } from '../../../util/positions'
import { AnalyzeGroupedPosition, GroupedPosition } from '../../../reducer/positions.reducer'
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { calculateGcd, ORDER_TYPES_STRATEGY_AUXILIARY } from '../../../util/orders'
import {
    getIcebergLegOrdersForSecurity,
    hasIncompleteIcebergOrdersInBatch,
    hasPlacedIcebergLegsForBatch,
    icebergLegsAllExecuted,
} from '../../../util/icebergOrders'
import TradeOrderPremium from './tradeOrderPremium'
import ThinArrowDownIcon from '../../../assets/icons/ThinArrowDownIcon'
import { useStyles } from './styles'
import {
    batchHasAnyPrimaryLegFill,
    basketBookHasOpenExposureForBasket,
    basketConditionalRowLabelFromStrategyGroup,
    basketPrimaryLegsAllAllowEntryRuleEdit,
    buildStrategySpreadConditionalSnapshot,
    conditionalEntryLegAllowsRuleEditInUi,
    conditionalExitLegAllowsRuleEditInUi,
    conditionalPrimaryUsesSpreadLhs,
    getNonAuxiliaryLegsInBatch,
    hasConditionalRuleContent,
    isConditionalEntryReady,
    resolveBatchIdForBasketFromOrders,
} from '../../../util/conditionalEntry'
import { getStrategyChartParamsFromTradeOrders } from '../../../util/common'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { securityKeysFromRefs } from '../../../hooks/useSecurityMarketTicks'
import BasketConditionalEntrySlot from './BasketConditionalEntrySlot'
import { BasketWeightedMarketParams } from './useBasketWeightedMarketPrices'

export const orderTypes = [
    {
        value: Broker_OrderType.LIMIT_ORDER,
        label: 'Limit Order'
    },
    {
        value: Broker_OrderType.MARKET_ORDER,
        label: 'Market Order'
    }
]

interface Props {
    hasSlAndTargetAccess: boolean;
    positions: Position[];
    onSlTargetValidationChange: (isValid: boolean) => void;
}

const TradeOrderAttributes: React.FC<Props> = (props) => {
    const { hasSlAndTargetAccess, positions, onSlTargetValidationChange } = props;
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const product = useSelector(selectTradeProduct)
    const orderType = useSelector(selectOrderType)
    const tradeOrders = useSelector(selectTradeOrders)
    const placedOrders = useSelector(selectOrdersHistory)
    const mode = useSelector(selectStrategyBuilderMode)
    const basketId = useSelector(selectBasketId)
    const batchId = useSelector(selectBatchId)
    /** Redux `batchId` is often empty when opening the panel for an existing basket; resolve from order history. */
    const effectiveBatchId = useMemo(
        () => batchId || resolveBatchIdForBasketFromOrders(placedOrders, basketId) || '',
        [batchId, placedOrders, basketId]
    )
    const basketPositionHasPrimaryFill = useMemo(
        () =>
            !!basketId &&
            batchHasAnyPrimaryLegFill(
                placedOrders || [],
                basketId,
                effectiveBatchId || undefined
            ),
        [basketId, placedOrders, effectiveBatchId]
    )
    const basketHasOpenBookExposure = useMemo(
        () => basketBookHasOpenExposureForBasket(positions, basketId),
        [positions, basketId]
    )
    const allowCxForBookOnlyBasketNoCompose = useMemo(
        () => tradeOrders.length === 0 && !!basketId && basketHasOpenBookExposure,
        [tradeOrders.length, basketId, basketHasOpenBookExposure]
    )
    /** With an existing (partial/full) basket position, CX stays in the panel only when the compose grid has no lines; if there are trade orders, use Positions or place CX with the order. */
    const hideBasketCxTradePanelWhenFormedPositionHasComposeRows = useMemo(
        () =>
            mode === 'trade' &&
            !!basketId &&
            basketPositionHasPrimaryFill &&
            tradeOrders.length > 0,
        [mode, basketId, basketPositionHasPrimaryFill, tradeOrders.length]
    )
    const waitingForOrders = useSelector(selectWaitingForOrders)
    const iceberg = useSelector(selectIceberg)
    const { primaryOrUnderlying: selectedSecurity } = useSelectPrimaryOrUnderlyingSecurity()
    const strategyGroupFromStore = useSelector(selectStrategyGroup)
    const basketConditionalEntry = useSelector(selectBasketConditionalEntry)
    const basketConditionalExit = useSelector(selectBasketConditionalExit)
    const [slTargetFieldValid, setSlTargetFieldValid] = useState(true)
    const [advancedMode, setAdvancedMode] = useState(false)
    const [localLegsValue, setLocalLegsValue] = useState<string>(iceberg.numberOfLegs.toString())

    const basketStrategyDisplayName = useMemo(() => {
        const und =
            selectedSecurity?.underlyingSecurityCode ??
            selectedSecurity?.symbol ??
            selectedSecurity?.code ??
            ''
        return basketConditionalRowLabelFromStrategyGroup(strategyGroupFromStore, und)
    }, [strategyGroupFromStore, selectedSecurity])

    const basketPosition = useMemo((): AnalyzeGroupedPosition | null => {
        // For analyze and squareOff modes, use getAnalyzedGroupedPosition
        if ((mode === 'analyze' || mode === 'squareOff') && basketId) {
            return getAnalyzedGroupedPosition(
                positions as unknown as Position[],
                basketId,
                placedOrders,
                tradeOrders
            )
        }
        // For trade mode, create a synthetic position from tradeOrders
        if (mode === 'trade' && tradeOrders?.length && basketId && selectedSecurity) {
            let quantities = []
            let totalPremium = 0
            const legMult = iceberg.enabled ? Math.max(1, iceberg.numberOfLegs) : 1
            tradeOrders.forEach(order => {
                const rawQty = order.quantity * legMult
                const orderQuantity = order.transactionType === TransactionType.BUY ? rawQty : -rawQty
                totalPremium += orderQuantity * (order.price ?? 0)
                quantities.push(orderQuantity)
            })
            const netQuantity = calculateGcd(quantities)
            if (netQuantity === 0) return null
            const avgPrice = Math.abs(totalPremium / netQuantity)
            return {
                groupId: basketId,
                groupName: basketStrategyDisplayName,
                quantity: netQuantity,
                avgPrice: avgPrice,
                realizedProfit: 0,
                security: selectedSecurity,
                children: []
            }
        }
        return null
    }, [
        mode,
        positions,
        basketId,
        placedOrders,
        tradeOrders,
        selectedSecurity,
        iceberg.enabled,
        iceberg.numberOfLegs,
        basketStrategyDisplayName,
    ])

    const orderIdToOrderMap = useMemo(() => {
        if (!placedOrders) return {} as Record<string, Order>
        return placedOrders.reduce(
            (acc, o) => {
                acc[o.orderId] = o
                return acc
            },
            {} as Record<string, Order>
        )
    }, [placedOrders])

    const onIcebergToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const enabled = e.target.checked;
        dispatch(updateIceberg({ enabled }));
        if (enabled) {
            dispatch(updateBasketConditionalExit({ enabled: false, condition: undefined }));
        }
        // Force limit order when iceberg is enabled
        if (enabled && orderType === Broker_OrderType.MARKET_ORDER) {
            dispatch(updateOrderType(Broker_OrderType.LIMIT_ORDER));
        }
    }, [dispatch, orderType]);
    
    const onIcebergLegsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.trim();
        setLocalLegsValue(raw);
        if (raw === "") return;
        const numValue = parseInt(raw, 10);
        if (Number.isNaN(numValue)) return;
        const clamped = Math.max(1, Math.min(10, numValue));
        dispatch(updateIceberg({ numberOfLegs: clamped }));
        if (String(clamped) !== raw) {
            setLocalLegsValue(String(clamped));
        }
    }, [dispatch]);

    const onIcebergLegsBlur = useCallback(() => {
        if (localLegsValue === "" || Number.isNaN(parseInt(localLegsValue, 10))) {
            setLocalLegsValue("1");
            dispatch(updateIceberg({ numberOfLegs: 1 }));
            return;
        }
        const n = Math.max(1, Math.min(10, parseInt(localLegsValue, 10)));
        setLocalLegsValue(String(n));
        dispatch(updateIceberg({ numberOfLegs: n }));
    }, [dispatch, localLegsValue]);

    useEffect(() => {
        setLocalLegsValue(String(iceberg.numberOfLegs));
    }, [iceberg.numberOfLegs]);

    const toggleAdvanced = useCallback(() => {
        setAdvancedMode(!advancedMode);
    }, [advancedMode]);

    const hasAnyIcebergLegsForBasketBatch = useMemo(
        () => hasPlacedIcebergLegsForBatch(placedOrders, effectiveBatchId, basketId),
        [placedOrders, basketId, effectiveBatchId]
    );

    /** Any working iceberg child for this basket+batch (modify / partial fill). */
    const hasIncompleteIcebergChildren = useMemo(
        () => hasIncompleteIcebergOrdersInBatch(placedOrders, effectiveBatchId, basketId),
        [placedOrders, basketId, effectiveBatchId]
    );

    const icebergStructureLocked = waitingForOrders && iceberg.enabled && hasAnyIcebergLegsForBasketBatch;

    const allExecuted = useMemo(() => {
        const selected = tradeOrders.filter((o) => o.selected);
        if (selected.length === 0) return false;
        return selected.every((o) => {
            const legs = getIcebergLegOrdersForSecurity(placedOrders || [], effectiveBatchId, basketId, o.security);
            if (legs.length > 0) {
                return icebergLegsAllExecuted(legs);
            }
            if (!o.referenceOrderId) return false;
            return orderIdToOrderMap[o.referenceOrderId]?.status === OrderStatus.EXECUTED;
        });
    }, [tradeOrders, placedOrders, effectiveBatchId, basketId, orderIdToOrderMap]);

    const icebergOrderAttrsLocked = iceberg.enabled || hasIncompleteIcebergChildren;
    const icebergForceLimitOrderType = iceberg.enabled || hasIncompleteIcebergChildren;

    const firstTradeLine = useMemo(() => {
        const sel = tradeOrders.filter((t) => t.selected)
        const list = sel.length ? sel : tradeOrders
        return list[0]
    }, [tradeOrders])

    const [firstLegSecurity, setFirstLegSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    /** SL/TARGET-aligned ref. security for CE/CX conditions + chart (underlying when F&O). */
    const [conditionalRefSecurity, setConditionalRefSecurity] =
        useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)

    useEffect(() => {
        const fromTrade = firstTradeLine?.security
        if (fromTrade?.code) {
            getSecurityAsync(getSecurityKey(fromTrade.exchange, fromTrade.code)).then(setFirstLegSecurity)
            return
        }
        if ((mode === 'analyze' || mode === 'squareOff') && basketPosition?.security?.code) {
            const kids = basketPosition.children
            let sec = basketPosition.security
            if (kids?.length) {
                const withOpen = kids.find((k) => {
                    const nq = (k as AnalyzeGroupedPosition).newQuantity
                    return (nq ?? k.quantity ?? 0) !== 0
                })
                sec = (withOpen ?? kids[0]).security
            }
            getSecurityAsync(getSecurityKey(sec.exchange, sec.code)).then(setFirstLegSecurity)
            return
        }
        setFirstLegSecurity(undefined)
    }, [firstTradeLine, mode, basketPosition])

    useEffect(() => {
        if (!selectedSecurity?.code) {
            setConditionalRefSecurity(undefined)
            return
        }
        getSecurityAsync(getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)).then(
            setConditionalRefSecurity
        )
    }, [selectedSecurity?.exchange, selectedSecurity?.code])

    /** CE/CX summary: strategy name when LHS is spread; else ref security (underlying for F&O CE/CX). */
    const basketConditionalRowDisplaySymbol = useCallback(
        (cond: Condition | undefined) => {
            if (conditionalPrimaryUsesSpreadLhs(cond)) return basketStrategyDisplayName
            return (
                conditionalRefSecurity?.symbol ??
                conditionalRefSecurity?.underlyingSecurityCode ??
                conditionalRefSecurity?.code ??
                basketStrategyDisplayName
            )
        },
        [basketStrategyDisplayName, conditionalRefSecurity]
    )

    const firstLegSnapshotKey = firstLegSecurity
        ? getSecurityKey(firstLegSecurity.exchange, firstLegSecurity.code)
        : ''
    const selectedTradeOrders = useMemo(() => {
        const sel = tradeOrders.filter((t) => t.selected)
        return sel.length ? sel : tradeOrders
    }, [tradeOrders])

    const basketPositionsForChart = useMemo(
        () => (positions ?? []).filter((p) => p.basketId === basketId),
        [positions, basketId]
    )

    const marketTickKeys = useMemo(() => {
        const refs: { exchange: number; code: string }[] = []
        if (firstLegSecurity) refs.push(firstLegSecurity)
        const sel = tradeOrders.filter((t) => t.selected)
        const lines = sel.length ? sel : tradeOrders
        lines.forEach((line) => line.security && refs.push(line.security))
        basketPositionsForChart.forEach((p) => refs.push(p.security))
        if (effectiveBatchId && basketId) {
            getNonAuxiliaryLegsInBatch(placedOrders || [], basketId, effectiveBatchId).forEach((o) => {
                if (o.security?.code) refs.push(o.security)
            })
        }
        if ((mode === 'analyze' || mode === 'squareOff') && basketPosition?.children?.length) {
            basketPosition.children.forEach((c) => refs.push(c.security))
        }
        return securityKeysFromRefs(refs)
    }, [
        firstLegSecurity,
        tradeOrders,
        basketPositionsForChart,
        effectiveBatchId,
        basketId,
        placedOrders,
        mode,
        basketPosition?.children,
    ])
    const basketWeightedPriceParams = useMemo((): BasketWeightedMarketParams => ({
        marketTickKeys,
        firstLegSnapshotKey,
        tradeOrders,
        placedOrders: placedOrders || [],
        basketId,
        effectiveBatchId,
        icebergLegMult: iceberg.enabled ? Math.max(1, iceberg.numberOfLegs) : 1,
        mode,
        basketPosition,
        firstLegSecurity,
    }), [
        marketTickKeys,
        firstLegSnapshotKey,
        tradeOrders,
        placedOrders,
        basketId,
        effectiveBatchId,
        iceberg.enabled,
        iceberg.numberOfLegs,
        mode,
        basketPosition,
        firstLegSecurity,
    ])

    const strategySpreadConditionalSnapshot = useMemo(
        () => buildStrategySpreadConditionalSnapshot(selectedTradeOrders, basketPositionsForChart),
        [selectedTradeOrders, basketPositionsForChart]
    )

    const basketConditionalModalChartRefAvailable = useMemo(() => {
        if (!strategySpreadConditionalSnapshot || !conditionalRefSecurity) return false
        const uKey = getSecurityKey(conditionalRefSecurity.exchange, conditionalRefSecurity.code)
        const { securities: keys } = getStrategyChartParamsFromTradeOrders(
            selectedTradeOrders,
            basketPositionsForChart
        )
        if (keys.length === 0) return false
        if (keys.length === 1 && keys[0] === uKey) return false
        return true
    }, [
        strategySpreadConditionalSnapshot,
        conditionalRefSecurity,
        selectedTradeOrders,
        basketPositionsForChart,
    ])

    const basketOpenCeOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            if (o.basketId !== basketId) return false
            if (
                ![OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status ?? 0)
            )
                return false
            if (o.orderType !== OrderType.CONDITIONAL_ENTRY) return false
            if (effectiveBatchId && o.batchId !== effectiveBatchId) return false
            return true
        })
    }, [placedOrders, basketId, effectiveBatchId])

    const basketInactiveCeOrder = useMemo(() => {
        return (placedOrders || []).find(
            (o) =>
                o.basketId === basketId &&
                o.status === OrderStatus.INACTIVE &&
                o.orderType === OrderType.CONDITIONAL_ENTRY &&
                !!effectiveBatchId &&
                o.batchId === effectiveBatchId
        )
    }, [placedOrders, basketId, effectiveBatchId])

    /** Basket CE and iceberg are mutually exclusive in trade. */
    const icebergDisabledByConditionalEntry = useMemo(
        () =>
            mode === 'trade' &&
            (basketConditionalEntry.enabled ||
                !!basketOpenCeOrder ||
                !!basketInactiveCeOrder),
        [mode, basketConditionalEntry.enabled, basketOpenCeOrder, basketInactiveCeOrder]
    )

    const basketOpenCxOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            if (o.basketId !== basketId) return false
            if (
                ![OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status ?? 0)
            )
                return false
            if (o.orderType !== OrderType.CONDITIONAL_EXIT) return false
            if (effectiveBatchId && o.batchId !== effectiveBatchId) return false
            return true
        })
    }, [placedOrders, basketId, effectiveBatchId])

    const basketInactiveCxOrder = useMemo(() => {
        return (placedOrders || []).find(
            (o) =>
                o.basketId === basketId &&
                o.status === OrderStatus.INACTIVE &&
                o.orderType === OrderType.CONDITIONAL_EXIT &&
                !!effectiveBatchId &&
                o.batchId === effectiveBatchId
        )
    }, [placedOrders, basketId, effectiveBatchId])

    const hadCeInCurrentBatch = useMemo(() => {
        if (!effectiveBatchId || !placedOrders?.length) return false
        return placedOrders.some(
            (o) =>
                o.basketId === basketId &&
                o.batchId === effectiveBatchId &&
                o.orderType === OrderType.CONDITIONAL_ENTRY
        )
    }, [placedOrders, basketId, effectiveBatchId])

    const hasBasketMainInCurrentBatch = useMemo(() => {
        if (!effectiveBatchId || !placedOrders?.length) return false
        return placedOrders.some(
            (o) =>
                o.basketId === basketId &&
                o.batchId === effectiveBatchId &&
                o.isBasket &&
                !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)
        )
    }, [placedOrders, basketId, effectiveBatchId])

    /** Any placed primary leg for this basket+batch (basket or single-leg `default_` ids). */
    const hasPlacedMainInCurrentBatch = useMemo(() => {
        if (!effectiveBatchId || !placedOrders?.length) return false
        return placedOrders.some(
            (o) =>
                o.basketId === basketId &&
                o.batchId === effectiveBatchId &&
                !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType)
        )
    }, [placedOrders, basketId, effectiveBatchId])

    const basketMainFillProgress = useMemo(
        () => batchHasAnyPrimaryLegFill(placedOrders || [], basketId, effectiveBatchId),
        [placedOrders, basketId, effectiveBatchId]
    )

    /** Basket only: cannot newly enable CE after placing main legs without CE (modify-with-CE still allowed). */
    const blockRetroactiveBasketCe = useMemo(
        () =>
            mode !== 'analyze' &&
            mode !== 'squareOff' &&
            waitingForOrders &&
            hasBasketMainInCurrentBatch &&
            !hadCeInCurrentBatch,
        [mode, waitingForOrders, hasBasketMainInCurrentBatch, hadCeInCurrentBatch]
    )

    /** Basket CE rule editable only while no primary is `OPEN` (live) and none partial / filled (see `primaryLegAllowsBasketConditionalEntryRuleEdit`). */
    const basketMainsUnfilledForCeEdit = useMemo(() => {
        if (!effectiveBatchId || !basketId) return true
        return basketPrimaryLegsAllAllowEntryRuleEdit(placedOrders || [], basketId, effectiveBatchId)
    }, [placedOrders, basketId, effectiveBatchId])

    const prevLiveCeOrderId = useRef<string | undefined>(undefined)
    useEffect(() => {
        const ref = basketOpenCeOrder ?? basketInactiveCeOrder
        const cond = ref?.orderExecutionParams?.condition
        const liveId = ref?.orderId
        if (ref && hasConditionalRuleContent(cond)) {
            if (prevLiveCeOrderId.current !== liveId) {
                dispatch(updateBasketConditionalEntry({ enabled: true, condition: cond }))
                prevLiveCeOrderId.current = liveId
            }
            return
        }
        prevLiveCeOrderId.current = undefined
        // Keep draft CE while composing a new basket; clear ghosts after place / in analyze
        if (mode === 'trade' && !waitingForOrders) return
        dispatch(updateBasketConditionalEntry({ enabled: false, condition: undefined }))
    }, [
        mode,
        waitingForOrders,
        basketOpenCeOrder?.orderId,
        basketInactiveCeOrder?.orderId,
        basketOpenCeOrder?.status,
        basketInactiveCeOrder?.status,
        basketOpenCeOrder?.orderExecutionParams,
        basketInactiveCeOrder?.orderExecutionParams,
        dispatch,
    ])

    const prevLiveCxOrderId = useRef<string | undefined>(undefined)
    useEffect(() => {
        const ref = basketOpenCxOrder ?? basketInactiveCxOrder
        const cond = ref?.orderExecutionParams?.condition
        const liveId = ref?.orderId
        if (ref && hasConditionalRuleContent(cond)) {
            // Bind once per live order id so user can clear for remove without hydrate fighting back
            if (prevLiveCxOrderId.current !== liveId) {
                dispatch(updateBasketConditionalExit({ enabled: true, condition: cond }))
                prevLiveCxOrderId.current = liveId
            }
            return
        }
        prevLiveCxOrderId.current = undefined
        // Keep draft CX while composing, or on Adjust with open book exposure (allow add without live CX)
        if (mode === 'trade' && !waitingForOrders) return
        if (
            (mode === 'analyze' || mode === 'squareOff') &&
            allowCxForBookOnlyBasketNoCompose
        ) {
            return
        }
        dispatch(updateBasketConditionalExit({ enabled: false, condition: undefined }))
    }, [
        mode,
        waitingForOrders,
        allExecuted,
        allowCxForBookOnlyBasketNoCompose,
        basketOpenCxOrder?.orderId,
        basketInactiveCxOrder?.orderId,
        basketOpenCxOrder?.status,
        basketInactiveCxOrder?.status,
        basketOpenCxOrder?.orderExecutionParams,
        basketInactiveCxOrder?.orderExecutionParams,
        dispatch,
    ])

    useEffect(() => {
        if (!blockRetroactiveBasketCe) return
        if (basketOpenCeOrder || basketInactiveCeOrder) return
        if (basketConditionalEntry.enabled || basketConditionalEntry.condition) {
            dispatch(updateBasketConditionalEntry({ enabled: false, condition: undefined }))
        }
    }, [
        blockRetroactiveBasketCe,
        basketOpenCeOrder?.orderId,
        basketInactiveCeOrder?.orderId,
        basketConditionalEntry.enabled,
        basketConditionalEntry.condition,
        dispatch,
    ])

    const conditionalEntryValid = useMemo(
        () => !basketConditionalEntry.enabled || isConditionalEntryReady(basketConditionalEntry),
        [basketConditionalEntry]
    )

    const cxOrderAllowsEdit = useMemo(
        () => conditionalExitLegAllowsRuleEditInUi(basketOpenCxOrder ?? basketInactiveCxOrder),
        [basketOpenCxOrder, basketInactiveCxOrder]
    )

    const ceOrderAllowsRuleEdit = useMemo(
        () => conditionalEntryLegAllowsRuleEditInUi(basketOpenCeOrder ?? basketInactiveCeOrder),
        [basketOpenCeOrder, basketInactiveCeOrder]
    )

    /**
     * Trade: allow CX on first compose, after batch mains exist in history (incl. open + zero fills), when any
     * main has fill progress, after all selected rows executed, or when a CX leg already exists.
     * Analyze / squareOff: CE stays hidden; CX when a live CX exists (edit/remove) or open book exposure
     * with empty compose grid (add exit on Adjust).
     */
    const liveBasketCxForUi = basketOpenCxOrder ?? basketInactiveCxOrder

    const cxAllowedByBatchLifecycle = useMemo(() => {
        if (mode === 'analyze' || mode === 'squareOff') {
            return !!liveBasketCxForUi || allowCxForBookOnlyBasketNoCompose
        }
        const existingCxLeg = liveBasketCxForUi
        return (
            allowCxForBookOnlyBasketNoCompose ||
            !waitingForOrders ||
            allExecuted ||
            !!existingCxLeg ||
            basketMainFillProgress ||
            hasPlacedMainInCurrentBatch
        )
    }, [
        mode,
        liveBasketCxForUi,
        waitingForOrders,
        allExecuted,
        basketMainFillProgress,
        hasPlacedMainInCurrentBatch,
        allowCxForBookOnlyBasketNoCompose,
    ])

    const allowConditionalExitUi = useMemo(
        () => {
            const analyzeLike = mode === 'analyze' || mode === 'squareOff'
            const tradeGridClearInAnalyze = analyzeLike ? tradeOrders.length === 0 : true
            return (
                tradeGridClearInAnalyze &&
                cxAllowedByBatchLifecycle &&
                cxOrderAllowsEdit &&
                (orderType === Broker_OrderType.LIMIT_ORDER || orderType === Broker_OrderType.MARKET_ORDER) &&
                !iceberg.enabled &&
                !hideBasketCxTradePanelWhenFormedPositionHasComposeRows
            )
        },
        [
            mode,
            tradeOrders.length,
            cxAllowedByBatchLifecycle,
            cxOrderAllowsEdit,
            orderType,
            iceberg.enabled,
            hideBasketCxTradePanelWhenFormedPositionHasComposeRows,
        ]
    )

    /** Show CX tile on Adjust when editable or when a live CX exists (may be view-only / disabled). */
    const showAnalyzeConditionalExitSlot = useMemo(
        () =>
            (mode === 'analyze' || mode === 'squareOff') &&
            (allowConditionalExitUi || !!liveBasketCxForUi),
        [mode, allowConditionalExitUi, liveBasketCxForUi]
    )

    const conditionalExitValid = useMemo(
        () => !basketConditionalExit.enabled || isConditionalEntryReady(basketConditionalExit),
        [basketConditionalExit]
    )

    useEffect(() => {
        if (!iceberg.enabled) return
        if (basketConditionalExit.enabled || basketConditionalExit.condition) {
            dispatch(updateBasketConditionalExit({ enabled: false, condition: undefined }))
        }
    }, [iceberg.enabled, basketConditionalExit.enabled, basketConditionalExit.condition, dispatch])

    useEffect(() => {
        if (!hideBasketCxTradePanelWhenFormedPositionHasComposeRows) return
        if (basketConditionalExit.enabled || basketConditionalExit.condition) {
            dispatch(updateBasketConditionalExit({ enabled: false, condition: undefined }))
        }
    }, [
        hideBasketCxTradePanelWhenFormedPositionHasComposeRows,
        basketConditionalExit.enabled,
        basketConditionalExit.condition,
        dispatch,
    ])

    useEffect(() => {
        onSlTargetValidationChange(
            slTargetFieldValid && conditionalEntryValid && conditionalExitValid
        )
    }, [slTargetFieldValid, conditionalEntryValid, conditionalExitValid, onSlTargetValidationChange])

    useEffect(() => {
        if (icebergForceLimitOrderType && orderType === Broker_OrderType.MARKET_ORDER) {
            dispatch(updateOrderType(Broker_OrderType.LIMIT_ORDER));
        }
    }, [icebergForceLimitOrderType, orderType, dispatch]);

    const onProductChange = useCallback(
        (e: any, value: Product) => {
            if (icebergOrderAttrsLocked) return;
            dispatch(updateTradeProduct(value));
        },
        [dispatch, icebergOrderAttrsLocked]
    );

    const onOrderTypeChange = useCallback(
        (event: any) => {
            if (icebergOrderAttrsLocked) return;
            const nextType = event.target.value as Broker_OrderType;
            if (icebergForceLimitOrderType && nextType === Broker_OrderType.MARKET_ORDER) return;
            AnalyticsManager.logEvent("Order Type Changed", {
                order_type: broker_OrderTypeToJSON(nextType),
                source: "Trade",
            });
            dispatch(updateOrderType(nextType));
        },
        [dispatch, icebergOrderAttrsLocked, icebergForceLimitOrderType]
    );

    const showAdvancedSection = useMemo(
        () =>
            Boolean(tradeOrders?.length) ||
            ((mode === 'analyze' || mode === 'squareOff') && basketPosition != null) ||
            (mode === 'trade' && allowCxForBookOnlyBasketNoCompose),
        [tradeOrders?.length, mode, basketPosition, allowCxForBookOnlyBasketNoCompose]
    );

    const prevAnalyzeLike = useRef(false)
    useEffect(() => {
        const analyzeLike = mode === 'analyze' || mode === 'squareOff'
        if (analyzeLike && !prevAnalyzeLike.current && basketPosition != null) {
            setAdvancedMode(true)
        }
        prevAnalyzeLike.current = analyzeLike
    }, [mode, basketPosition])

    const prevTradeBookOnlyCx = useRef(false)
    useEffect(() => {
        const tradeBookOnly = mode === 'trade' && allowCxForBookOnlyBasketNoCompose
        if (tradeBookOnly && !prevTradeBookOnlyCx.current) {
            setAdvancedMode(true)
        }
        prevTradeBookOnlyCx.current = tradeBookOnly
    }, [mode, allowCxForBookOnlyBasketNoCompose])

    const shouldDisableSlTarget = useMemo(() => !hasSlAndTargetAccess, [hasSlAndTargetAccess])

    return (
        <Grid px={1} container gap={1}>
            <> 
            <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                        PRODUCT
                    </Typography>
                    <ToggleButtonGroup
                        id="product-type-group"
                        size="small"
                        value={product}
                        exclusive
                        aria-label="product type"
                        fullWidth
                        disabled={icebergOrderAttrsLocked}
                        sx={{
                            height: '32px',
                            '.MuiButtonBase-root': {
                                borderColor: (theme) => theme.palette.divider
                            }
                        }}
                        onChange={onProductChange}
                    >
                        <NonInteractiveTooltip title="MIS" arrow placement="top">
                                <ToggleButton
                                    aria-label="mis"
                                    value={Product.INTRADAY}
                                    sx={{
                                        color: (theme) => (product === Product.INTRADAY ? `${theme.palette.text.primary} !important` : `${theme.palette.text.secondary} !important`)
                                    }}
                                >
                                    Intraday
                                </ToggleButton>
                        </NonInteractiveTooltip>
                        <NonInteractiveTooltip title="NRML" arrow placement="top">
                                <ToggleButton
                                    aria-label="overnight"
                                    value={Product.MARGIN}
                                    sx={{
                                        color: (theme) => (product === Product.MARGIN ? `${theme.palette.text.primary} !important` : theme.palette.text.secondary)
                                    }}
                                >
                                    Overnight
                                </ToggleButton>
                        </NonInteractiveTooltip>
                    </ToggleButtonGroup>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                        ORDER TYPE
                    </Typography>
                    <Select
                        value={orderType}
                        size="small"
                        fullWidth
                        // sx={{ fontWeight: 500, ...customStyle.select, height: '32px' }}
                        sx={{ height: '32px' }}
                        disabled={icebergOrderAttrsLocked}
                        onChange={onOrderTypeChange}
                    >
                        {orderTypes
                            .filter((o) => !icebergForceLimitOrderType || o.value === Broker_OrderType.LIMIT_ORDER)
                            .map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>
            <TradeOrderPremium disabled={!tradeOrders?.length} />
            {hasSlAndTargetAccess && (
                <SlAndTargetInputs
                    disabled={shouldDisableSlTarget}
                    disabledCheckbox={false}
                    basketPosition={basketPosition}
                    strategyMode={mode}
                    onValidationChange={setSlTargetFieldValid}
                />
            )}
            
            {/* Advanced Section — also in analyze/squareOff when a basket position exists (trade rows may be empty). */}
            {showAdvancedSection && (
                <Grid container item xs={12} marginTop={-2}>
                    <Grid item xs={12} textAlign="center" marginBottom={advancedMode ? 0 : 1}>
                        <span style={{fontWeight: 400}} role="button" className={classes.advancedButton} onClick={toggleAdvanced}>
                            Advanced <ThinArrowDownIcon className={advancedMode ? classes.advancedActive : undefined} />
                        </span>
                    </Grid>
                    
                    {advancedMode && (
                        <Grid container item xs={12} spacing={1}>
                            {mode === 'trade' && (
                                <Grid item xs={12}>
                                    <Stack spacing={1.5} width="100%" alignItems="stretch">
                                        {/* Row 1: iceberg only */}
                                        <Stack spacing={0.5} sx={{ width: '100%' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={iceberg.enabled}
                                                        onChange={onIcebergToggle}
                                                        size="small"
                                                        disabled={
                                                            allExecuted ||
                                                            icebergStructureLocked ||
                                                            hasIncompleteIcebergChildren ||
                                                            icebergDisabledByConditionalEntry
                                                        }
                                                        sx={{ padding: 0, marginLeft: 0, marginRight: 0.5 }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" color="text.secondary">
                                                        NUMBER OF LEGS
                                                    </Typography>
                                                }
                                            />
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={localLegsValue}
                                                onChange={onIcebergLegsChange}
                                                onBlur={onIcebergLegsBlur}
                                                disabled={
                                                    !iceberg.enabled ||
                                                    allExecuted ||
                                                    icebergStructureLocked ||
                                                    hasIncompleteIcebergChildren ||
                                                    icebergDisabledByConditionalEntry
                                                }
                                                inputProps={{ min: 1, max: 10 }}
                                                sx={{ height: '32px', '& .MuiInputBase-root': { height: '32px' } }}
                                            />
                                        </Stack>
                                        {/* Row 2: CE | CX side by side when panel > 400px; else stacked (container query) */}
                                        {hasSlAndTargetAccess && conditionalRefSecurity && (
                                            <Box
                                                sx={{
                                                    containerType: 'inline-size',
                                                    width: '100%',
                                                    minWidth: 0,
                                                }}
                                            >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                    width: '100%',
                                                    minWidth: 0,
                                                    '@container (min-width: 401px)': {
                                                        flexDirection: 'row',
                                                        alignItems: 'flex-start',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        minWidth: 0,
                                                        flex: '1 1 auto',
                                                        '@container (min-width: 401px)': {
                                                            flex: '1 1 0%',
                                                        },
                                                    }}
                                                >
                                                    <BasketConditionalEntrySlot
                                                        priceParams={basketWeightedPriceParams}
                                                        entryProps={{
                                                        variant: 'entry',
                                                        security: conditionalRefSecurity,
                                                        conditionSecurity: conditionalRefSecurity,
                                                        displaySymbol: basketConditionalRowDisplaySymbol(
                                                            basketConditionalEntry.condition
                                                        ),
                                                        conditionalModalChartVariants:
                                                            basketConditionalModalChartRefAvailable &&
                                                            strategySpreadConditionalSnapshot
                                                                ? {
                                                                      spreadSnapshot: strategySpreadConditionalSnapshot,
                                                                      initialRef: 'spread',
                                                                  }
                                                                : undefined,
                                                        priceMultiplier: conditionalRefSecurity.priceMultiplier ?? 100,
                                                        disabled: allExecuted || icebergOrderAttrsLocked,
                                                        allowEnableConditionalEntry:
                                                            (orderType === Broker_OrderType.LIMIT_ORDER ||
                                                                orderType === Broker_OrderType.MARKET_ORDER) &&
                                                            !iceberg.enabled &&
                                                            !allExecuted &&
                                                            !blockRetroactiveBasketCe &&
                                                            basketMainsUnfilledForCeEdit &&
                                                            ceOrderAllowsRuleEdit &&
                                                            (!waitingForOrders ||
                                                                !hasPlacedMainInCurrentBatch ||
                                                                allExecuted),
                                                        lockRemoveCondition: !!basketOpenCeOrder,
                                                        value: basketConditionalEntry,
                                                        onChange: (next) =>
                                                            dispatch(updateBasketConditionalEntry(next)),
                                                        compact: true,
                                                        tradePanelAdvanced: true,
                                                        }}
                                                    />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        minWidth: 0,
                                                        flex: '1 1 auto',
                                                        '@container (min-width: 401px)': {
                                                            flex: '1 1 0%',
                                                        },
                                                    }}
                                                >
                                                    <BasketConditionalEntrySlot
                                                        priceParams={basketWeightedPriceParams}
                                                        entryProps={{
                                                        variant: 'exit',
                                                        security: conditionalRefSecurity,
                                                        conditionSecurity: conditionalRefSecurity,
                                                        displaySymbol: basketConditionalRowDisplaySymbol(
                                                            basketConditionalExit.condition
                                                        ),
                                                        conditionalModalChartVariants:
                                                            basketConditionalModalChartRefAvailable &&
                                                            strategySpreadConditionalSnapshot
                                                                ? {
                                                                      spreadSnapshot: strategySpreadConditionalSnapshot,
                                                                      initialRef: 'spread',
                                                                  }
                                                                : undefined,
                                                        priceMultiplier: conditionalRefSecurity.priceMultiplier ?? 100,
                                                        disabled: icebergOrderAttrsLocked || !allowConditionalExitUi,
                                                        allowEnableConditionalEntry: allowConditionalExitUi,
                                                        // Allow remove of live CX (Analyze / trade) → cancel on place
                                                        lockRemoveCondition: false,
                                                        value: basketConditionalExit,
                                                        onChange: (next) =>
                                                            dispatch(updateBasketConditionalExit(next)),
                                                        compact: true,
                                                        tradePanelAdvanced: true,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            </Box>
                                        )}
                                    </Stack>
                                </Grid>
                            )}
                            {showAnalyzeConditionalExitSlot &&
                                hasSlAndTargetAccess &&
                                conditionalRefSecurity && (
                                    <Grid item xs={12}>
                                        <BasketConditionalEntrySlot
                                            priceParams={basketWeightedPriceParams}
                                            entryProps={{
                                            variant: 'exit',
                                            security: conditionalRefSecurity,
                                            conditionSecurity: conditionalRefSecurity,
                                            displaySymbol: basketConditionalRowDisplaySymbol(
                                                basketConditionalExit.condition
                                            ),
                                            conditionalModalChartVariants:
                                                basketConditionalModalChartRefAvailable &&
                                                strategySpreadConditionalSnapshot
                                                    ? {
                                                          spreadSnapshot: strategySpreadConditionalSnapshot,
                                                          initialRef: 'spread',
                                                      }
                                                    : undefined,
                                            priceMultiplier: conditionalRefSecurity.priceMultiplier ?? 100,
                                            disabled: icebergOrderAttrsLocked || !allowConditionalExitUi,
                                            allowEnableConditionalEntry: allowConditionalExitUi,
                                            lockRemoveCondition: false,
                                            value: basketConditionalExit,
                                            onChange: (next) => dispatch(updateBasketConditionalExit(next)),
                                            compact: true,
                                            tradePanelAdvanced: true,
                                            }}
                                        />
                                    </Grid>
                                )}
                        </Grid>
                    )}
                </Grid>
            )}
            </>
        </Grid>
    )
}

export default memo(TradeOrderAttributes)